import React, { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Link } from "react-router-dom";
import { LayoutDashboard, ShoppingCart, Package, Layers, Users, Boxes, Ticket, Star, BookOpen, Settings, Bell, ScrollText, BarChart3, LogOut, ExternalLink } from "lucide-react";
import { useStore } from "../../context/StoreContext";
import api, { img } from "../../lib/api";

const NAV = [
  ["/admin", "Overview", LayoutDashboard, true],
  ["/admin/orders", "Orders", ShoppingCart],
  ["/admin/products", "Products", Package],
  ["/admin/inventory", "Inventory", Boxes],
  ["/admin/collections", "Collections", Layers],
  ["/admin/customers", "Customers", Users],
  ["/admin/coupons", "Coupons", Ticket],
  ["/admin/reviews", "Reviews", Star],
  ["/admin/journal", "Journal", BookOpen],
  ["/admin/analytics", "Analytics", BarChart3],
  ["/admin/notifications", "Notifications", Bell],
  ["/admin/audit", "Audit Logs", ScrollText],
  ["/admin/settings", "Settings", Settings],
];

export default function AdminLayout() {
  const { user, authReady, logout, settings } = useStore();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);

  useEffect(() => {
    if (authReady && (!user || user.role === "customer")) nav("/login");
  }, [authReady, user]);
  useEffect(() => {
    if (user && user.role !== "customer") api.get("/admin/notifications").then(r => setUnread(r.data.unread)).catch(() => {});
  }, [user]);

  if (!user || user.role === "customer") return null;

  return (
    <div className="grid lg:grid-cols-[250px_1fr] min-h-screen bg-ivory">
      <aside className="hidden lg:flex flex-col border-r border-sage/30 bg-ivory sticky top-0 h-screen">
        <div className="p-6 border-b border-sage/30">
          {settings.logo_light ? <img src={img(settings.logo_light)} alt="the IAH" className="h-9 w-auto mb-1" /> : <div className="serif text-3xl tracking-[0.15em]">IAH</div>}
          <div className="text-[10px] uppercase tracking-[0.2em] text-forest/50">Admin Console</div>
        </div>
        <nav className="flex-1 overflow-auto p-3 space-y-0.5">
          {NAV.map(([to, label, Icon, end]) => (
            <NavLink key={to} to={to} end={end} data-testid={`admin-nav-${label.toLowerCase().replace(" ", "-")}`}
              className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 text-sm rounded-sm transition-colors ${isActive ? "bg-forest text-ivory" : "text-forest/70 hover:bg-beige/60"}`}>
              <Icon size={16} /> <span className="flex-1">{label}</span>
              {label === "Notifications" && unread > 0 && <span className="bg-brown text-ivory text-[10px] px-1.5 rounded-full">{unread}</span>}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-sage/30 space-y-1">
          <Link to="/" target="_blank" className="flex items-center gap-3 px-3 py-2.5 text-sm text-forest/70 hover:bg-beige/60"><ExternalLink size={16} /> View Store</Link>
          <button onClick={() => { logout(); nav("/login"); }} className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-forest/70 hover:bg-beige/60" data-testid="admin-logout"><LogOut size={16} /> Logout</button>
        </div>
      </aside>

      {/* mobile top bar */}
      <div className="lg:hidden fixed top-0 inset-x-0 z-40 bg-ivory border-b border-sage/30 flex items-center justify-between px-4 h-14">
        <span className="serif text-2xl tracking-widest">IAH Admin</span>
        <button onClick={() => { logout(); nav("/login"); }}><LogOut size={18} /></button>
      </div>

      <main className="p-5 lg:p-10 pt-20 lg:pt-10 overflow-x-hidden">
        {/* mobile nav scroll */}
        <div className="lg:hidden flex gap-2 overflow-x-auto hide-scrollbar mb-6 -mx-1 px-1">
          {NAV.map(([to, label, Icon, end]) => (
            <NavLink key={to} to={to} end={end} className={({ isActive }) => `flex-shrink-0 px-3 py-2 text-xs rounded-sm ${isActive ? "bg-forest text-ivory" : "bg-beige/60 text-forest/70"}`}>{label}</NavLink>
          ))}
        </div>
        <Outlet />
      </main>
    </div>
  );
}

export const AdminHeader = ({ title, subtitle, action }) => (
  <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
    <div><h1 className="serif text-4xl">{title}</h1>{subtitle && <p className="text-forest/55 text-sm mt-1">{subtitle}</p>}</div>
    {action}
  </div>
);
